import { useCallback, useId, useMemo, useRef, useState } from 'react';

/**
 * Options for {@link useRovingRadioGroup}.
 */
export interface UseRovingRadioGroupOptions {
  /** Number of `role="radio"` options in the group. */
  itemCount: number;
  /**
   * Index of the currently selected option, or `-1` when nothing is selected.
   * When nothing is selected the first enabled option is the group's tab stop,
   * matching the WAI-ARIA radio pattern.
   */
  selectedIndex: number;
  /** Called with the index that should become selected as focus moves. */
  onSelect: (index: number) => void;
  /**
   * Whether arrowing past the last/first option wraps around. Defaults to
   * `true` (the behaviour the APG radio pattern allows).
   */
  wrap?: boolean;
  /** Returns true for an option that cannot be selected and must be skipped. */
  isItemDisabled?: (index: number) => boolean;
  /**
   * When true (the default), selection follows focus: arrowing to an option
   * selects it immediately via `onSelect`, matching the APG radio pattern that
   * suits an always-visible inline group.
   *
   * Set false where selecting an option *dismisses the surface* — a bottom sheet
   * that closes on pick. There, selection-follows-focus means the first arrow
   * press both moves and commits, closing the sheet before the user can reach
   * another option (PB-5434). In this mode arrows move focus only, the roving tab
   * stop follows focus, and selection commits on Enter/Space (or a row click).
   */
  selectOnFocus?: boolean;
}

/** Props to spread on the `role="radiogroup"` container element. */
export interface RovingRadioGroupProps {
  ref: React.RefObject<HTMLDivElement | null>;
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
}

/** Props to spread on each `role="radio"` option element. */
export interface RovingRadioItemProps {
  'data-roving-index': number;
  /** Scopes the item to its own group so nested groups don't cross-match. */
  'data-roving-group': string;
  tabIndex: 0 | -1;
}

const NEXT_KEYS = new Set(['ArrowRight', 'ArrowDown']);
const PREV_KEYS = new Set(['ArrowLeft', 'ArrowUp']);

/**
 * Wires the keyboard behaviour a `role="radiogroup"` promises assistive
 * technology: arrow keys move focus (and, following the APG pattern, selection)
 * between options, Home/End jump to the ends, and the group exposes a single
 * tab stop via roving `tabindex`. Selection follows focus by default, so
 * `onSelect` fires as the user arrows and `aria-checked` tracks it. Pass
 * `selectOnFocus: false` for a surface that closes on pick (a bottom sheet): then
 * arrows move focus only and Enter/Space commits (see {@link UseRovingRadioGroupOptions.selectOnFocus}).
 *
 * ## Why this exists
 * The repo's shared `Radio`/`RadioGroup` and six hand-rolled pickers declared
 * `role="radio"` but implemented none of this, so keyboard and screen-reader
 * users could not move between options even though the role told them to
 * (PB-5434). The hidden native `<input type="radio">` inside `Radio` is
 * `aria-hidden`/`tabIndex={-1}`, so the browser never supplied the behaviour
 * either — it has to be implemented here, once, for every group to share.
 *
 * The hook owns only focus/selection movement and the roving tabindex. Space
 * and Enter are left to each option's native `<button>` (or `Radio`'s own
 * handler), so this hook never needs to preventDefault them.
 *
 * @param options see {@link UseRovingRadioGroupOptions}
 * @returns `groupProps` for the container and `getItemProps(index)` for each option
 */
export function useRovingRadioGroup({
  itemCount,
  selectedIndex,
  onSelect,
  wrap = true,
  isItemDisabled,
  selectOnFocus = true,
}: UseRovingRadioGroupOptions): {
  groupProps: RovingRadioGroupProps;
  getItemProps: (index: number) => RovingRadioItemProps;
} {
  const containerRef = useRef<HTMLDivElement>(null);
  // Stable id scoping this group's items. Nested radiogroups (a RadioGroup
  // rendered inside a hook-driven picker) share the outer container's DOM
  // subtree, so the focus lookup and the keydown origin match on this id rather
  // than on DOM containment alone — a nested group's item is contained too.
  const groupId = useId();

  const isDisabled = useCallback(
    (index: number) => (isItemDisabled ? isItemDisabled(index) : false),
    [isItemDisabled]
  );

  const firstEnabled = useMemo(() => {
    for (let i = 0; i < itemCount; i += 1) {
      if (!isDisabled(i)) return i;
    }
    return -1;
  }, [itemCount, isDisabled]);

  const lastEnabled = useMemo(() => {
    for (let i = itemCount - 1; i >= 0; i -= 1) {
      if (!isDisabled(i)) return i;
    }
    return -1;
  }, [itemCount, isDisabled]);

  // The option in the page tab order when selection follows focus: the selected
  // option when valid and enabled, otherwise the first enabled one so a fresh
  // group is still reachable with Tab. If the selected index points at a disabled
  // option the tab stop moves to the first enabled option while selection stays
  // put — aria-checked and the tab stop can diverge, which is the correct
  // trade-off: a disabled option must never be a tab stop.
  const selectionTabbable =
    selectedIndex >= 0 && selectedIndex < itemCount && !isDisabled(selectedIndex)
      ? selectedIndex
      : firstEnabled;

  // In move-only mode (selectOnFocus === false) focus and selection diverge until
  // the user activates, so the roving tab stop follows the focused option instead.
  // Seeded from the selection and re-seeded whenever the selection changes from
  // outside the group (e.g. a row click) — done during render with a previous-
  // value compare (React's pattern for resetting state on a prop change) rather
  // than an effect, so it never triggers a cascading render. Arrowing moves focus
  // without changing selectionTabbable, so it never re-seeds.
  const [focusedIndex, setFocusedIndex] = useState(selectionTabbable);
  const [seenSelection, setSeenSelection] = useState(selectionTabbable);
  if (selectionTabbable !== seenSelection) {
    setSeenSelection(selectionTabbable);
    if (!selectOnFocus) setFocusedIndex(selectionTabbable);
  }

  const tabbableIndex = selectOnFocus ? selectionTabbable : focusedIndex;

  const step = useCallback(
    (from: number, dir: 1 | -1): number => {
      if (firstEnabled === -1) return from;
      let i = from;
      for (let n = 0; n < itemCount; n += 1) {
        i += dir;
        if (i < 0 || i >= itemCount) {
          if (!wrap) return from;
          i = (i + itemCount) % itemCount;
        }
        if (!isDisabled(i)) return i;
      }
      return from;
    },
    [itemCount, wrap, isDisabled, firstEnabled]
  );

  const moveTo = useCallback(
    (index: number) => {
      // Scope to THIS group's items so a nested group's same-index item is never
      // the focus target. groupId is a useId() value (e.g. ":r0:"); the colons
      // are valid inside a quoted attribute-selector value.
      const el = containerRef.current?.querySelector<HTMLElement>(
        `[data-roving-group="${groupId}"][data-roving-index="${index}"]`
      );
      // Focus first so the move is immediate even before the re-render lands;
      // focus() works regardless of the element's tabIndex.
      el?.focus();
      if (selectOnFocus) {
        onSelect(index);
      } else {
        // Move focus only; the roving tab stop follows via focusedIndex and
        // selection waits for an explicit Enter/Space/click.
        setFocusedIndex(index);
      }
    },
    [groupId, onSelect, selectOnFocus]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      const origin = (e.target as HTMLElement).closest<HTMLElement>('[data-roving-index]');
      // Ignore the key when focus is on a non-radio control (no data-roving-index
      // — e.g. an "Other" chip or a sort-direction toggle), or on an item that
      // belongs to a NESTED/other roving group that bubbled up to this container.
      // Match on this group's id, not DOM containment: a nested item is contained.
      if (!origin || origin.getAttribute('data-roving-group') !== groupId) return;
      const current = Number(origin.getAttribute('data-roving-index'));
      if (Number.isNaN(current)) return;

      // Move-only mode commits the focused option on Enter/Space; in the default
      // mode these are left to the option's own handler (see the class doc).
      if (!selectOnFocus && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onSelect(current);
        return;
      }

      let next: number | null = null;
      if (NEXT_KEYS.has(e.key)) next = step(current, 1);
      else if (PREV_KEYS.has(e.key)) next = step(current, -1);
      else if (e.key === 'Home') next = firstEnabled;
      else if (e.key === 'End') next = lastEnabled;
      else return;

      e.preventDefault();
      if (next !== null && next >= 0 && next !== current) {
        moveTo(next);
      }
    },
    [groupId, step, firstEnabled, lastEnabled, moveTo, selectOnFocus, onSelect]
  );

  const getItemProps = useCallback(
    (index: number): RovingRadioItemProps => ({
      'data-roving-index': index,
      'data-roving-group': groupId,
      tabIndex: index === tabbableIndex ? 0 : -1,
    }),
    [groupId, tabbableIndex]
  );

  return {
    groupProps: { ref: containerRef, onKeyDown },
    getItemProps,
  };
}
